import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'

import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { ComposerStore as ZustandComposerStore } from '../composerStore'
import type {
  ChannelVariantContent,
  OmniAsset,
  ThreadItem,
} from '../postSlice/types'
import { checkChannelParityAfterMirror } from './channelDrift'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'
import {
  cloneChannelData,
  cloneRetweet,
  cloneSourceLink,
  projectFluxChannelContent,
  projectFluxThreadItems,
} from './projectFluxChannelContent'
import { projectFluxOmniAssets } from './projectFluxOmniContent'

/**
 * Deep-cloning attached media and per-item thread assets (once per selected
 * channel) is the expensive pass, so it is debounced off the keystroke path;
 * the cheaper scalar/small-array fields stay synchronous. Thread structure
 * changes at commit-event frequency (switch/add/delete/reorder/AI-split), not
 * keystroke frequency, so it rides this pass alongside media rather than the
 * synchronous one. `maxWait` bounds staleness under continuous emission.
 */
const ASSET_MIRROR_DEBOUNCE_MS = 150
const ASSET_MIRROR_MAX_WAIT_MS = 1000

/**
 * Bounds the seed follow-up's re-arm chain (see below) so a persistent,
 * uncorrectable mismatch can't keep it re-scheduling every tick forever.
 */
const MAX_SEED_FOLLOW_UP_ATTEMPTS = 20

export interface FluxToChannelVariantsMirror {
  unsubscribe: () => void
  /**
   * Apply any pending (debounced) asset/thread-items pass immediately, so a
   * just-attached asset or a just-committed thread item is not left in the
   * debounce window. Also resolves a pending seed follow-up synchronously
   * (see the seed/content-copy race on `installFluxToChannelVariantsMirror`),
   * so a settle-point caller does not have to wait for its timer either.
   * Registered via `registerChannelMirrorFlush` for the save path to call.
   */
  flush: () => void
}

/** The per-service Flux draft backing a selected channel, or undefined. */
function draftForService(serviceName: string): Draft | undefined {
  return ComposerStore.getDraft(serviceName)
}

/**
 * Keep Zustand's `postSlice.channelVariants` in sync with the legacy Flux
 * per-service drafts, keyed by `channelId` (channelId → profile → service draft;
 * channels sharing a service mirror the same draft). Flux → Zustand only.
 *
 * Selection lives in `AppStore`, so this listens to both stores and drops a
 * variant when its channel is deselected. On install it reconciles once
 * synchronously, so the collection matches the current selection before any
 * reader observes it (this also re-seeds correctly on a mid-session flag flip).
 * The mirror splits by cost: cheap scalar/small-array fields (text, urls,
 * aiAssisted, updateType, isReminder, title, enabledAttachmentType,
 * channelData, sourceLink, retweet, selectedStickers) sync on every emission;
 * the expensive per-item cloning (media assets and thread items,
 * both of which deep-clone nested media) is debounced. A newly-created
 * variant is seeded with its full projection (assets and thread items
 * included) so it is never briefly observable with empty assets.
 *
 * Seed/content-copy race: a freshly-selected channel's variant is seeded from
 * whatever its Flux draft looks like the instant `AppStore`'s selection
 * change is observed — but `ComposerStore`'s own content copy into a
 * newly-enabled draft (e.g. copying omni content in when auto-exiting omni
 * mode on the first channel selection) can land moments later, on a Flux
 * dispatch that deliberately skips its own `emitChange()` (a UI re-render
 * optimization, not a data-correctness signal — see `enableDraft`'s
 * `shouldPreventRerender`), or on a separately-queued dispatch entirely (a
 * channel group enables each member via its own `setTimeout`, so their
 * content copies and attachment-type inference land on staggered ticks, not
 * all at once). One deferred re-check isn't enough to cover an arbitrary
 * number of staggered ticks, so `scheduleSeedFollowUp` re-arms itself as long
 * as a pass keeps finding something to correct, bounded by
 * `MAX_SEED_FOLLOW_UP_ATTEMPTS`.
 *
 * @see documentation/work-notes/2026-08-04-composer-flux-to-zustand-bridge.md
 */
export function installFluxToChannelVariantsMirror(
  store: ZustandComposerStore,
): FluxToChannelVariantsMirror {
  // Stamped once per mirror install, so a drift report can tell how long this
  // composer session had been running — see `checkChannelParityAfterMirror`.
  // `performance.now()`, not `Date.now()`, since the wall clock can jump
  // (system time adjustment, NTP sync) across a session long enough to drift.
  const installedAt = performance.now()
  let pendingSeedFollowUp: ReturnType<typeof setTimeout> | null = null

  // Re-runs both halves of the mirror shortly after a seed or a scalar
  // correction, re-arming itself (up to `MAX_SEED_FOLLOW_UP_ATTEMPTS` times)
  // as long as a pass keeps finding something to write — a still-in-flight
  // staggered dispatch shows up as another correction on a later tick, not
  // necessarily another brand-new variant. Calls `syncProjectedMediaFields`
  // directly rather than through the debounced wrapper, for a definite,
  // immediate catch-up.
  const scheduleSeedFollowUp = (attempt = 0): void => {
    if (pendingSeedFollowUp !== null) return
    pendingSeedFollowUp = setTimeout(() => {
      pendingSeedFollowUp = null
      if (isMirrorWriteInProgress()) return
      const wroteAnything = syncScalarFields()
      syncProjectedMediaFields()
      if (wroteAnything && attempt < MAX_SEED_FOLLOW_UP_ATTEMPTS) {
        scheduleSeedFollowUp(attempt + 1)
      }
    }, 0)
  }

  /**
   * Removes any variant whose channel is no longer selected, without
   * touching scalar field corrections. Used standalone at teardown (see
   * `unsubscribe`) so a deselect whose emitChange is still in flight doesn't
   * leave its variant behind, without also opportunistically picking up an
   * unrelated stale-field mutation the way a full `syncScalarFields` pass
   * would (see the seed/content-copy race this mirror already documents).
   */
  const removeDeselectedVariants = (): void => {
    const current = store.getState().channelVariants
    const selectedIds = new Set(
      AppStore.getSelectedProfiles()
        .filter((profile) => draftForService(profile.service.name))
        .map((profile) => profile.id),
    )
    const removals = Object.keys(current).filter((id) => !selectedIds.has(id))
    if (removals.length === 0) return
    runMirrorWrite(() => {
      const actions = store.getState()
      for (const channelId of removals) {
        actions.removeChannelVariant(channelId)
      }
    })
  }

  /** Returns whether this pass seeded a variant or corrected an existing one. */
  const syncScalarFields = (): boolean => {
    const current = store.getState().channelVariants
    const selectedIds = new Set<string>()
    const writes: Array<[string, Partial<ChannelVariantContent>]> = []
    let wroteAnything = false

    for (const profile of AppStore.getSelectedProfiles()) {
      const draft = draftForService(profile.service.name)
      if (!draft) continue
      selectedIds.add(profile.id)

      const variant = current[profile.id]
      if (!variant) {
        // Seed a new variant with the full projection (assets included) so a
        // reader never sees it with empty assets before the debounced pass.
        writes.push([profile.id, projectFluxChannelContent(draft)])
        wroteAnything = true
        continue
      }

      const partial: Partial<ChannelVariantContent> = {}
      if (draft.text !== variant.text) partial.text = draft.text
      if (!isEqual(draft.urls, variant.urls)) partial.urls = [...draft.urls]
      if (!isEqual(draft.unshortenedUrls, variant.unshortenedUrls))
        partial.unshortenedUrls = [...draft.unshortenedUrls]
      if (draft.aiAssisted !== variant.aiAssisted)
        partial.aiAssisted = draft.aiAssisted
      if (draft.updateType !== variant.updateType)
        partial.updateType = draft.updateType
      const isReminder = draft.isReminder ?? false
      if (isReminder !== variant.isReminder) partial.isReminder = isReminder
      if (draft.title !== variant.title) partial.title = draft.title
      if (draft.enabledAttachmentType !== variant.enabledAttachmentType)
        partial.enabledAttachmentType = draft.enabledAttachmentType
      // Diff against the raw reference (cheap; the bag is small and flat) and
      // clone only when writing, since the write is what needs freeze-safety.
      if (!isEqual(draft.channelData, variant.channelData))
        partial.channelData = cloneChannelData(draft.channelData)
      if (!isEqual(draft.sourceLink, variant.sourceLink))
        partial.sourceLink = cloneSourceLink(draft.sourceLink)
      if (!isEqual(draft.retweet, variant.retweet))
        partial.retweet = cloneRetweet(draft.retweet)
      if (!isEqual(draft.selectedStickers, variant.selectedStickers))
        partial.selectedStickers = [...draft.selectedStickers]

      if (Object.keys(partial).length > 0) {
        writes.push([profile.id, partial])
        wroteAnything = true
      }
    }

    const removals = Object.keys(current).filter((id) => !selectedIds.has(id))

    if (writes.length === 0 && removals.length === 0) return wroteAnything
    runMirrorWrite(() => {
      const actions = store.getState()
      for (const [channelId, partial] of writes) {
        actions.syncChannelVariantFromFlux(channelId, partial)
      }
      for (const channelId of removals) {
        actions.removeChannelVariant(channelId)
      }
    })
    return wroteAnything
  }

  /**
   * The debounced half of the mirror: projects and diffs the two fields whose
   * projection deep-clones nested media (assets, and thread items via their
   * own per-item assets), writing only what changed.
   */
  const syncProjectedMediaFields = (): void => {
    const current = store.getState().channelVariants
    const assetWrites: Array<[string, OmniAsset[]]> = []
    const threadItemWrites: Array<[string, ThreadItem[]]> = []

    for (const profile of AppStore.getSelectedProfiles()) {
      const draft = draftForService(profile.service.name)
      if (!draft) continue

      const variant = current[profile.id]

      const projectedAssets = projectFluxOmniAssets(draft)
      if (!variant || !isEqual(projectedAssets, variant.assets)) {
        assetWrites.push([profile.id, projectedAssets])
      }

      const projectedThreadItems = projectFluxThreadItems(draft)
      if (!variant || !isEqual(projectedThreadItems, variant.threadItems)) {
        threadItemWrites.push([profile.id, projectedThreadItems])
      }
    }

    if (assetWrites.length > 0 || threadItemWrites.length > 0) {
      runMirrorWrite(() => {
        const actions = store.getState()
        for (const [channelId, assets] of assetWrites) {
          actions.syncChannelVariantFromFlux(channelId, { assets })
        }
        for (const [channelId, threadItems] of threadItemWrites) {
          actions.syncChannelVariantFromFlux(channelId, { threadItems })
        }
      })
    }

    // Runs at the end of every settled pass — the one moment every mirrored
    // field is expected to agree.
    checkChannelParityAfterMirror(
      store,
      'fluxToZustand',
      performance.now() - installedAt,
    )
  }

  const debouncedSyncProjectedMediaFields = debounce(
    syncProjectedMediaFields,
    ASSET_MIRROR_DEBOUNCE_MS,
    {
      leading: true,
      trailing: true,
      maxWait: ASSET_MIRROR_MAX_WAIT_MS,
    },
  )

  const handleChange = (): void => {
    if (isMirrorWriteInProgress()) return
    const wroteAnything = syncScalarFields()
    debouncedSyncProjectedMediaFields()
    if (wroteAnything) scheduleSeedFollowUp()
  }

  // Reconcile synchronously before listening so the collection already matches
  // the current selection (assets and thread items included) when the mirror
  // is installed.
  if (syncScalarFields()) scheduleSeedFollowUp()
  syncProjectedMediaFields()

  ComposerStore.addChangeListener(handleChange)
  AppStore.addChangeListener(handleChange)

  const cancelPendingSeedFollowUp = (): void => {
    if (pendingSeedFollowUp === null) return
    clearTimeout(pendingSeedFollowUp)
    pendingSeedFollowUp = null
  }

  return {
    flush: (): void => {
      // A pending seed follow-up may be catching up media/thread-item
      // changes that never reached the debounced wrapper (no accompanying
      // Flux emission), so its catch-up runs directly here rather than
      // through `debouncedSyncProjectedMediaFields.flush()`, which only
      // re-invokes if the wrapper itself was called since — nothing to
      // flush when the race is precisely that it wasn't.
      if (pendingSeedFollowUp !== null) {
        cancelPendingSeedFollowUp()
        syncScalarFields()
        syncProjectedMediaFields()
      }
      debouncedSyncProjectedMediaFields.flush()
    },
    unsubscribe: (): void => {
      cancelPendingSeedFollowUp()
      ComposerStore.removeChangeListener(handleChange)
      AppStore.removeChangeListener(handleChange)
      // Prune against the current selection one last time: AppStore defers
      // its own emitChange by a setTimeout(0) (see onDispatchedPayload), so a
      // deselect whose dispatch already ran but whose emission hasn't fired
      // yet would otherwise never reach the removal logic in
      // `syncScalarFields`, leaving the variant behind with no listener left
      // alive to catch up later. This mirror instance is the last chance.
      removeDeselectedVariants()
      // Apply (not cancel) any pending pass so the last Flux state before
      // teardown is not lost to Zustand readers.
      debouncedSyncProjectedMediaFields.flush()
    },
  }
}
